import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { intlShape } from '../../util/reactIntl';
import { ResponsiveImage, ToolLogo } from '../../components';
import classNames from 'classnames';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { GoBriefcase } from 'react-icons/go';
import { FaRegBuilding } from 'react-icons/fa';
import config from '../../config';
import { getEmbedElement } from '../../util/videoEmbed';
import { skillOrToolLabel } from '../../util/skillAndToolLabels';

import css from './PortfolioWorkModalContent.css';

function extractFormElementsFromValues(values) {
  // Collect all keys that match text_N, video_N, or addImage_N
  const dynamicFieldRegex = /^(text|video|addImage)_(\d+)$/;
  const elements = [];

  // 1. Add elements from field keys (including addImage_N even if no dynamicImageData)
  Object.keys(values).forEach(key => {
    const match = key.match(dynamicFieldRegex);
    if (match) {
      let type;
      if (match[1] === 'text') type = 'FieldTextEditor';
      else if (match[1] === 'video') type = 'FieldTextInput';
      else if (match[1] === 'addImage') type = 'AddImages';
      const index = parseInt(match[2], 10);
      elements.push({ type, id: key, index });
    }
  });

  // 2. Add AddImages elements for all indexes in dynamicImageData (if not already present)
  if (values.dynamicImageData && Array.isArray(values.dynamicImageData)) {
    const addImageIndexes = new Set(values.dynamicImageData.map(d => d.elementIndex));
    addImageIndexes.forEach(index => {
      if (!elements.find(e => e.type === 'AddImages' && e.index === index)) {
        elements.push({ type: 'AddImages', id: `addImage_${index}`, index });
      }
    });
  }

  // 3. Sort by index to preserve order
  elements.sort((a, b) => a.index - b.index);
  return elements;
}

const PortfolioWorkModalContent = props => {
  const { work, skillOptions = [], toolOptions = [], intl } = props;
  const [formElements, setFormElements] = useState([]);

  const locale = config.locale;
  const translationKey = locale === 'fi' ? 'fiTranslation' : 'enTranslation';

  const publicData = work?.attributes?.publicData || {};
  const images = work?.images || [];
  const author = work?.author || {};
  const title = work?.attributes?.title || '';

  useEffect(() => {
    if (
      publicData &&
      (Object.keys(publicData).some(key => /^(text|video|addImage)_\d+$/.test(key)) ||
        publicData.dynamicImageData?.length > 0)
    ) {
      const elements = extractFormElementsFromValues(publicData);
      setFormElements(elements);
    } else {
      setFormElements([]);
    }
  }, [publicData]);

  const experience = publicData?.experience || [];
  const tools = publicData?.tools || [];
  const dynamicImageData = publicData?.dynamicImageData || [];
  const dynamicImageIds = dynamicImageData.map(d => d.imageId);
  const imageRadius = publicData?.imageRadius || false;

  const relatedWorkCustomerType = publicData?.relatedWorkCustomerType || '';
  const relatedWorkExperienceCustomer = publicData?.relatedWorkExperienceCustomer || '';
  const relatedWorkExperienceAgency = publicData?.relatedWorkExperienceAgency || '';
  const jobContinues = publicData?.jobContinues || false;
  const startMonth = publicData?.startMonth?.label || null;
  const startYear = publicData?.startYear?.label || null;
  const endMonth = publicData?.endMonth?.label || null;
  const endYear = publicData?.endYear?.label || null;

  const dynamicImages = images.filter(
    image => image && image.id && dynamicImageIds && dynamicImageIds.includes(image.id.uuid)
  );

  const heroImage = images.filter(
    image => image && image.id && dynamicImageIds && !dynamicImageIds.includes(image.id.uuid)
  );
  // If no dynamic images, use the first image as hero
  if (heroImage.length === 0 && images.length > 0 && images[0] && images[0].id) {
    heroImage.push(images[0]);
  }

  const getImageVariants = (image, dynamicImageData) => {
    const currentAspect =
      image && image.id ? dynamicImageData.find(d => d.imageId === image.id.uuid)?.aspect : null;
    const aspectRatio = currentAspect > 1 ? 'landscape' : currentAspect < 1 ? 'portrait' : 'square';
    const aspectVariants = {
      landscape: ['horizontal', 'horizontal-2x'],
      portrait: ['vertical', 'vertical-2x'],
      square: ['square', 'square2x'],
    };
    return currentAspect ? aspectVariants[aspectRatio] : aspectVariants.landscape;
  };

  // Combine skills and tools; only tools get a logo. Items without a label yet
  // (lists still loading) are left out instead of showing raw keys.
  const skillsAndTools = [...experience, ...tools.map(t => ({ ...t, isTool: true }))]
    .filter(item => item && item.category)
    .map(item => ({
      ...item,
      label: skillOrToolLabel(item.category, [skillOptions, toolOptions], translationKey),
    }))
    .filter(item => item.label);

  const freelancerDisplayName = author?.attributes?.profile?.displayName || '';

  return (
    <div className={css.contentContainer}>
      <div className={css.heroContainer}>
        <div className={css.heroInformationContainer}>
          {/* Header with title and author */}
          <div className={css.headerRow}>
            <div className={css.titleAuthorSection}>
              <div className={css.titleAuthorInfo}>
                <h1 className={css.title}>{title}</h1>
                <span className={css.displayName}>{freelancerDisplayName}</span>
              </div>
            </div>
          </div>

          {/* Info cards section */}
          <div className={css.infoCardsContainer}>
            {/* Top row: Agency, Client, Timeline */}
            {(relatedWorkExperienceCustomer ||
              relatedWorkExperienceAgency ||
              (startMonth && startYear)) && (
              <div className={css.infoCardsTopRow}>
                {/* Agency Card (if applicable) */}
                {relatedWorkCustomerType === 'agency' && relatedWorkExperienceAgency && (
                  <div className={css.infoCardCompact}>
                    <div className={css.infoCardIconWrapper}>
                      <FaRegBuilding className={css.infoCardIcon} />
                    </div>
                    <div className={css.infoCardValue}>Agency: {relatedWorkExperienceAgency}</div>
                  </div>
                )}

                {/* Client Card */}
                {relatedWorkExperienceCustomer && relatedWorkCustomerType && (
                  <div className={css.infoCardCompact}>
                    <div className={css.infoCardIconWrapper}>
                      <GoBriefcase className={css.infoCardIcon} />
                    </div>
                    <div className={css.infoCardValue}>{relatedWorkExperienceCustomer}</div>
                  </div>
                )}

                {/* Timeline Card */}
                {startMonth && startYear && (
                  <div className={css.infoCardCompact}>
                    <div className={css.infoCardIconWrapper}>
                      <IoCalendarClearOutline className={css.infoCardIcon} />
                    </div>
                    <div className={css.infoCardValue}>
                      {startMonth}/{startYear}
                      {jobContinues ? (
                        <span> - Ongoing</span>
                      ) : (
                        endMonth &&
                        endYear && (
                          <span>
                            {' '}
                            - {endMonth}/{endYear}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bottom row: Skills and Tools */}
            {skillsAndTools.length > 0 && (
              <div className={css.skillsRow}>
                {skillsAndTools.map(({ category, isTool, label }) => (
                  <div key={category} className={isTool ? css.toolContainer : css.skillContainer}>
                    {isTool && (
                      <ToolLogo
                        logoUrl={toolOptions.find(t => t && t.key === category)?.logoUrl}
                        className={css.toolLogo}
                      />
                    )}
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={css.heroImageContainer}>
        {heroImage[0] && (
          <ResponsiveImage
            rootClassName={classNames(css.rootForImage, imageRadius ? css.imageWithRadius : '')}
            alt={title}
            image={heroImage[0]}
            variants={['horizontal', 'horizontal-2x']}
          />
        )}
      </div>

      <div className={css.dynamicElementsContainer}>
        {formElements.map((element, index) => {
          if (element.type === 'FieldTextEditor') {
            return (
              <div key={element.id} className={css.textEditorContainer}>
                <div
                  className={css.textEditorContent}
                  dangerouslySetInnerHTML={{ __html: publicData[element.id] || '' }}
                />
              </div>
            );
          } else if (element.type === 'FieldTextInput') {
            const url = publicData[element.id];
            const embedElement = getEmbedElement(url);

            return (
              <div key={element.id} className={css.videoContainer}>
                {embedElement ? (
                  <div
                    className={classNames(
                      css.videoPreview,
                      url.includes('instagram.com/reel/') ? css.instagramContainer : '',
                      imageRadius ? css.imageWithRadius : ''
                    )}
                  >
                    {embedElement}
                  </div>
                ) : null}
              </div>
            );
          } else if (element.type === 'AddImages') {
            const elementImages = dynamicImages.filter(img => {
              const mapping = dynamicImageData.find(d => d.imageId === (img.id?.uuid || img.id));
              if (mapping) {
                return mapping.elementIndex === element.index;
              }
              // Fallback: show only in the first AddImages element
              const firstAddImagesIndex = formElements.findIndex(e => e.type === 'AddImages');
              return index === firstAddImagesIndex;
            });
            return (
              <div key={element.id} className={css.imagesContainer}>
                {elementImages
                  .filter(image => image && image.id && image.id.uuid)
                  .map(image => (
                    <ResponsiveImage
                      rootClassName={classNames(
                        css.rootForImage,
                        imageRadius ? css.imageWithRadius : ''
                      )}
                      key={image.id.uuid}
                      alt="Dynamic image"
                      image={image}
                      variants={getImageVariants(image, dynamicImageData)}
                    />
                  ))}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

PortfolioWorkModalContent.propTypes = {
  work: PropTypes.object.isRequired,
  skillOptions: PropTypes.array,
  toolOptions: PropTypes.array,
  intl: intlShape.isRequired,
};

export default PortfolioWorkModalContent;
